import React, { FC, useContext } from 'react';
import { Row, Column, FormInput } from 'Shared/components';
import { useFormikContext } from 'formik';
import { CoveredPartyPayloadType } from '@clearcover/core-app-types';
import { editDriverLicensePermissionsPolicy } from 'Admin/authPolicies';
import { PolicyContext } from 'Policies/utils';
import { STATE_OPTIONS } from 'Shared/constants/states';
import { env } from 'Shared/constants/env';
import { DriverInfoOptionsType, DriverOptionQuestionsConfigType } from '.';

import styles from './styles.module.css';
import { isNonLicensed } from './utils';

type DriverLicenseProps = {
  info: DriverInfoOptionsType;
  driverOptionsQuestionsConfig: DriverOptionQuestionsConfigType;
};

export const DriverLicense: FC<DriverLicenseProps> = ({
  info,
  driverOptionsQuestionsConfig,
}) => {
  const { resourceEffectiveGrants } = useContext(PolicyContext);
  const {
    handleBlur,
    handleChange,
    setFieldValue,
    values,
    touched,
    errors,
  } = useFormikContext<CoveredPartyPayloadType>();

  const { licenseStatus, licenseUserReportedStatus } = info ?? {};

  const { askForLicenseType, askForUserReportedLicenseStatus } =
    driverOptionsQuestionsConfig ?? {};

  // Edit Driver Form will have an identifier value
  // Add Driver Form will have an undefined identifier
  const isEditFormType = Boolean(values.identifier);

  // enable if add driver form or if you have permissions
  const canEditLicense =
    !isEditFormType ||
    editDriverLicensePermissionsPolicy(resourceEffectiveGrants);

  const licenseTypeRenderer = () => {
    return (
      <Column className={styles.driverInfoCol} variant="third">
        <FormInput
          variant="selectAutocomplete"
          name="info.licenseType"
          label="Driver's License Type"
          data-testid="license-type"
          onChange={(value: string) => {
            setFieldValue('info.licenseType', value);
            if (value === 'learners' || value === 'foreign') {
              setFieldValue('info.licenseStatus', '');
              setFieldValue('attributes.ageFirstLicensed', '');
              setFieldValue('info.licenseNumber', '');
              setFieldValue('info.licenseState', '');
              setFieldValue('reports.driversLicense.status', undefined);
            }
          }}
          onBlur={handleBlur}
          value={values.info.licenseType}
          errorMessage={
            touched.info?.licenseType ? errors.info?.licenseType : undefined
          }
          options={{
            us_license: 'U.S. License',
            foreign: 'Foreign',
            learners: 'U.S. Learner’s Permit',
          }}
          disabled={!askForLicenseType || !env.driverNewQuestionsEnabled}
        />
      </Column>
    );
  };

  const licenseNumberRenderer = () => {
    const { licenseUserReportedStatus } = values.info ?? {};
    const nonLicensed = isNonLicensed(licenseUserReportedStatus);

    if (nonLicensed) return null;

    return (
      <Column className={styles.driverInfoCol}>
        <FormInput
          variant="text"
          name="info.licenseNumber"
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.info.licenseNumber}
          errorMessage={
            touched.info?.licenseNumber ? errors.info?.licenseNumber : undefined
          }
          label="Driver License Number"
          data-testid="license-number"
          disabled={!canEditLicense}
        />
      </Column>
    );
  };

  const licenseStateRenderer = () => {
    const { licenseUserReportedStatus } = values.info ?? {};
    const nonLicensed = isNonLicensed(licenseUserReportedStatus);

    if (nonLicensed) return null;

    return (
      <Column className={styles.driverInfoCol}>
        <FormInput
          variant="selectAutocomplete"
          name="info.licenseState"
          data-testid="license-state"
          onChange={(value: string) => {
            setFieldValue('info.licenseState', value);
          }}
          onBlur={handleBlur}
          value={values.info.licenseState}
          errorMessage={
            touched.info?.licenseState ? errors.info?.licenseState : undefined
          }
          label="Driver License Issued State"
          options={STATE_OPTIONS}
          disabled={!canEditLicense}
        />
      </Column>
    );
  };

  const licenseStatusRenderer = () => (
    <Column className={styles.driverInfoCol}>
      <FormInput
        variant="selectAutocomplete"
        name="info.licenseStatus"
        data-testid="license-status"
        onChange={(value: string) => setFieldValue('info.licenseStatus', value)}
        onBlur={handleBlur}
        value={values.info.licenseStatus}
        errorMessage={
          touched.info?.licenseStatus ? errors.info?.licenseStatus : undefined
        }
        label="Driver License Status"
        options={licenseStatus}
        disabled={!canEditLicense}
      />
    </Column>
  );

  const licenseUserReportedStatusRenderer = () => (
    <Column className={styles.driverInfoCol} variant="third">
      <FormInput
        variant="selectAutocomplete"
        name="info.licenseUserReportedStatus"
        label="License Status"
        data-testid="user-reported-status"
        onChange={(value: string) => {
          setFieldValue('info.licenseUserReportedStatus', value);
        }}
        onBlur={handleBlur}
        value={licenseUserReportedStatus[values.info.licenseUserReportedStatus]}
        options={licenseUserReportedStatus}
        errorMessage={
          touched.info?.licenseUserReportedStatus
            ? errors.info?.licenseUserReportedStatus
            : undefined
        }
      />
    </Column>
  );

  const ageFirstLicensedRenderer = () => (
    <Column className={styles.driverInfoCol}>
      <FormInput
        variant="text"
        name="attributes.ageFirstLicensed"
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.attributes.ageFirstLicensed}
        errorMessage={
          touched.attributes?.ageFirstLicensed
            ? errors.attributes?.ageFirstLicensed
            : undefined
        }
        label="Age First Licensed"
        data-testid="age-first-licensed"
      />
    </Column>
  );

  return (
    <div className={styles.section} data-testid="driver-modal-license">
      <h1>Driver's License</h1>
      <Row className="fs-exclude">
        {!askForUserReportedLicenseStatus && licenseTypeRenderer()}
        {askForUserReportedLicenseStatus && (
          <>
            {licenseUserReportedStatusRenderer()}
            {licenseNumberRenderer()}
            {licenseStateRenderer()}
          </>
        )}
        {values.info.licenseType === 'us_license' && (
          <>
            {licenseNumberRenderer()}
            {licenseStateRenderer()}
          </>
        )}
        {values.info.licenseType === 'learners' && (
          <>
            <Column className={styles.driverInfoCol}></Column>
            <Column className={styles.driverInfoCol}></Column>
          </>
        )}
        {values.info.licenseType === 'foreign' && (
          <Column className={styles.driverInfoCol}>
            <div className={styles.info}>
              Please send proof of foreign driver’s license to
              underwriting-verification@clearcover.com within the next ten (10)
              days. If a valid U.S. driver’s license is acquired by this driver,
              please notify Clearcover.
            </div>
          </Column>
        )}
      </Row>
      {values.info.licenseType === 'us_license' && (
        <Row className="fs-exclude">
          {licenseStatusRenderer()}
          {ageFirstLicensedRenderer()}
          <Column className={styles.driverInfoCol}></Column>
        </Row>
      )}
    </div>
  );
};
